import classNames from "classnames/bind";
import React, { ReactNode, type PropsWithChildren } from "react";
import { Card } from "../../Card/Card";
import { Skeleton } from "../../Skeleton";
import { Tooltip } from "../../Tooltip";
import { extractDeepestChildText } from "../../utils/dom";
import { useIsOverflow } from "../../utils/hooks/useIsOverflow";
import { ListCardActionsMenu } from "./ListCardActionsMenu";
import styles from "./ListCard.module.scss";
import { ListCardProps, type FieldSegmentProps } from "./types";
import { useListCardActionsMenu } from "./useListCardActionsMenu";
const cx = classNames.bind(styles);

const FieldSegment = ({ children, className }: FieldSegmentProps) => {
  return <div className={className}>{children}</div>;
};

const TitleFieldSegment = (
  props: PropsWithChildren<{
    className?: string;
  }>
) => (
  <FieldSegment
    className={cx("title-segment", "clamp", props.className)}
    {...props}
  />
);

const DescriptionFieldSegment = (
  props: PropsWithChildren<{
    className?: string;
  }>
) => (
  <FieldSegment
    className={cx("description-segment", "clamp", props.className)}
    {...props}
  />
);

const MetaFieldSegment = (
  props: PropsWithChildren<{
    className?: string;
  }>
) => (
  <FieldSegment
    className={cx("meta-segment", props.className)}
    {...props}
  />
);

/**
 * A Card with actions that serves as a vertically-arranged listing mechanism for items with a few fields.
 */
const ListCard = ({
  renderTitle,
  renderDescription,
  renderMeta,
  actions,
  actionsMenuContentProps,
  actionsMenuOpen,
  onActionsMenuOpenChange,
  action,
  isSelected = false,
  isDisabled = false,
  isLoading = false,
  isBusy = false,
  hasBorderBottom = true,
  colorVariant = "default",
  className,
  style,
  onClick,
  as: asProp,
  onMouseLeave: onContainerMouseLeave,
  onMouseEnter: onContainerMouseEnter,
  ...restProps
}: ListCardProps) => {
  const Tag = asProp ?? "div";
  const hasActionsMenu = Boolean(actions?.length) || Boolean(action) || isBusy;

  const titleFieldSegment: ReactNode = renderTitle({ FieldSegment });
  const descriptionFieldSegment: ReactNode = renderDescription?.({
    FieldSegment,
  });
  const title = extractDeepestChildText(titleFieldSegment);
  const description = extractDeepestChildText(descriptionFieldSegment);

  const [isOverflowing, handleOverflowRef] = useIsOverflow();
  const {
    triggerRef,
    isActionsPinnedVisible,
    suppressActionsReveal,
    isMenuOpen,
    isMenuOpenControlled,
    handleMenuOpenChange,
    resetActionsReveal,
    menuContentProps,
  } = useListCardActionsMenu({
    actionsMenuContentProps,
    actionsMenuOpen,
    onActionsMenuOpenChange,
    isBusy,
  });

  const handleContainerMouseEnter: NonNullable<
    ListCardProps["onMouseEnter"]
  > = (event) => {
    resetActionsReveal();
    onContainerMouseEnter?.(event);
  };

  const handleContainerMouseLeave: NonNullable<
    ListCardProps["onMouseLeave"]
  > = (event) => {
    resetActionsReveal();
    onContainerMouseLeave?.(event);
  };

  return (
    <Tag
      className={cx(
        "container",
        suppressActionsReveal && "container--actions-suppressed"
      )}
      tabIndex={0}
      onMouseEnter={handleContainerMouseEnter}
      onMouseLeave={handleContainerMouseLeave}
      {...restProps}
    >
      <Card
        className={cx(
          "list-card",
          colorVariant && `list-card--${colorVariant}`,
          isDisabled && "is-disabled",
          isLoading && "is-loading",
          isBusy && "is-busy",
          isSelected && "is-selected",
          hasBorderBottom && "has-border-bottom",
          onClick && "has-on-click",
          className
        )}
        style={style}
        onClick={onClick}
      >
        <div className={cx("main", hasActionsMenu && "has-actions")}>
          <div>
            <Skeleton
              isLoading={isLoading}
              isBusy={isBusy}
            >
              <div
                ref={handleOverflowRef}
                className={cx("field-container")}
              >
                {isOverflowing ? (
                  <Tooltip
                    title={title}
                    content={description}
                    position={["bottom", "top", "left", "right"]}
                  >
                    {renderTitle({ FieldSegment: TitleFieldSegment })}
                  </Tooltip>
                ) : (
                  <>{renderTitle({ FieldSegment: TitleFieldSegment })}</>
                )}
              </div>
            </Skeleton>
            {renderDescription ? (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
              >
                <div
                  ref={handleOverflowRef}
                  className={cx("field-container", "description-field")}
                >
                  {renderDescription({ FieldSegment: DescriptionFieldSegment })}
                </div>
              </Skeleton>
            ) : null}
            {renderMeta ? (
              <Skeleton
                isLoading={isLoading}
                isBusy={isBusy}
              >
                <div
                  ref={handleOverflowRef}
                  className={cx("field-container", "meta-field")}
                >
                  {renderMeta({ FieldSegment: MetaFieldSegment })}
                </div>
              </Skeleton>
            ) : null}
          </div>

          {hasActionsMenu ? (
            <ListCardActionsMenu
              actions={actions}
              action={action}
              isBusy={isBusy}
              isLoading={isLoading}
              isActionsPinnedVisible={isActionsPinnedVisible}
              isMenuOpen={isMenuOpen}
              isMenuOpenControlled={isMenuOpenControlled}
              menuContentProps={menuContentProps}
              onMenuOpenChange={handleMenuOpenChange}
              triggerRef={triggerRef}
            />
          ) : null}
        </div>
      </Card>
    </Tag>
  );
};

export default ListCard;
